import { PagenatedResponse, SearchPagenateQuery } from '@models/common';
import { PaymentStatementSettlementStatus, RefundStatus } from '@models/paymentStatement';
import { ProductOrderViewModel } from '@models/productOrderView';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createSafeStorage, createSizePartialize } from '@utils/localStorage';

const PAGE_SIZE = 20;

interface ProductOrdersContainer {
  contents: ProductOrderViewModel[];
  page: number;
  size: number;
  total: number;
  hasNext: boolean;
}

export interface ProductOrdersViewQuery extends SearchPagenateQuery {
  buyerId?: string;
  paymentStatus?: string;
  dateType?: string;
  saleType?: string;
  refundStatus?: RefundStatus;
  isSettlementTarget?: boolean;
  settlementStatus?: PaymentStatementSettlementStatus;
  channel?: string;
  isPartnerRefundRequested?: boolean;
  completionStatus?: string;
  marketSection?: string;
}

interface ProductOrdersStoreProps {
  searchResultContainer: ProductOrdersContainer;
  searchQuery: ProductOrdersViewQuery;
  searchKeyword?: string;
  setStartDate: (date: string | null) => void;
  setEndDate: (date: string | null) => void;
  setSearchKeyword: (keyword: string) => void;
  updateSearchQuery: (query: { [key: string]: any }) => void;
  resetSearchQuery: () => void;
  setProductOrdersContainer: (result: PagenatedResponse<ProductOrderViewModel>) => void;
}

export const useProductOrdersStore = create(
  persist<ProductOrdersStoreProps>(
    (set) => ({
      searchResultContainer: {
        contents: [] as ProductOrderViewModel[],
        total: 0,
        page: 0,
        size: 0,
        hasNext: false,
      },
      searchQuery: {
        page: 0,
        size: PAGE_SIZE,
        dateType: 'payment.paidAt',
        startDate: undefined,
        endDate: undefined,
        isSettlementTarget: false,
        channel: 'all',
        marketSection: 'all',
      },
      setStartDate: (date: string | null) => {
        set((state) => {
          return { ...state, searchQuery: { ...state.searchQuery, startDate: date } };
        });
      },
      setEndDate: (date: string | null) => {
        set((state) => {
          return { ...state, searchQuery: { ...state.searchQuery, endDate: date } };
        });
      },
      searchKeyword: undefined,
      setProductOrdersContainer: (result) => {
        set((state) => {
          // 메모리 효율성을 위해 실제 데이터만 저장
          const newValue = {
            ...state.searchResultContainer,
            ...result,
            contents: result?.contents || [],
          };
          return { ...state, searchResultContainer: newValue };
        });
      },
      setSearchKeyword: (keyword: string) => {
        set((state) => {
          return {
            ...state,
            searchKeyword: keyword,
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      updateSearchQuery: (query: { [key: string]: any }) => {
        set((state) => {
          return {
            ...state,
            searchQuery: { ...state.searchQuery, page: 0, ...query },
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      resetSearchQuery: () => {
        set({
          searchQuery: {
            page: 0,
            size: PAGE_SIZE,
            dateType: 'payment.paidAt',
            startDate: undefined,
            endDate: undefined,
            isSettlementTarget: false,
            keyword: '',
            channel: 'all',
            marketSection: 'all',
            isPartnerRefundRequested: false,
          },
        });
      },
    }),
    {
      name: 'igogo-admin-product-orders',
      storage: createSafeStorage(),
      partialize: createSizePartialize<ProductOrdersStoreProps>(100), // 최대 100개 항목만 저장
    },
  ),
);
